import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { completeLogin } from "../auth/keycloak";
import { homePath } from "../auth/roles";
import { getUserRole, logout } from "../utils/auth";
import { takeReturnPath } from "../auth/returnPath";
import { Button, Loading } from "./ui";

/**
 * Where Keycloak sends the browser back after login. Swaps the code for tokens,
 * then sends the user to the page they first asked for, or their role's home page.
 */
const AuthCallback = () => {
  const nav = useNavigate();
  const [error, setError] = useState(null);
  // Signed in at Keycloak, but the account holds no HECMIS role, so no page would let them in
  const [noRole, setNoRole] = useState(false);
  // React 18 runs effects twice in development; the code may only be spent once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    completeLogin()
      .then((user) => {
        if (!user) {
          setError("Sign-in could not be completed. Please try again.");
          return;
        }
        if (!getUserRole()) {
          console.warn("Signed in, but the token carries no HECMIS role; the account needs one in Keycloak");
          setNoRole(true);
          return;
        }
        // The page they tried to open before signing in, otherwise their role's home
        nav(takeReturnPath() ?? homePath(), { replace: true });
      })
      .catch((err) => {
        console.error("Keycloak sign-in failed:", err);
        setError(err?.message || "Sign-in failed. Please try again.");
      });
  }, [nav]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      {noRole ? (
        <div className="flex max-w-md flex-col items-center gap-4 rounded-xl bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Your account has no access yet</h1>
          <p className="text-sm text-slate-600">
            You are signed in, but your account has not been given a role in HECMIS (for example HEC Evaluator or Scholarship Owner). Ask an HEC administrator to assign
            one, then sign in again.
          </p>
          <Button onClick={logout}>Sign out</Button>
        </div>
      ) : error ? (
        <div className="flex max-w-md flex-col items-center gap-4 rounded-xl bg-white p-6 text-center shadow-sm">
          <p className="rounded-md bg-red-50 p-2 text-sm text-red-600">{error}</p>
          <Button onClick={() => nav("/", { replace: true })}>Back to sign in</Button>
        </div>
      ) : (
        <Loading label="Signing you in…" />
      )}
    </div>
  );
};

export default AuthCallback;
